import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';

const RECORDING_ENABLED =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_SESSION_RECORDING === '1';

const Panel = styled.div`
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 9999;
  background: rgba(30, 58, 138, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  padding: 12px 16px;
  color: white;
  font-size: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const Button = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: white;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DownloadLink = styled.a`
  color: #93c5fd;
  font-size: 13px;
  &:hover {
    text-decoration: underline;
  }
`;

const SessionRecorderInner: React.FC = () => {
  const [recording, setRecording] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    setDownloadUrl(null);
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: 'browser' as const,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stopStream();
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setDownloadUrl(url);
      };
      recorder.start(1000);
      setRecording(true);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to start recording');
      stopStream();
    }
  }, [stopStream]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      recorderRef.current = null;
      setRecording(false);
    }
  }, [recording]);

  const clearDownload = useCallback(() => {
    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
      setDownloadUrl(null);
    }
  }, [downloadUrl]);

  if (!RECORDING_ENABLED) return null;

  return (
    <Panel>
      <span style={{ fontWeight: 600 }}>Session recording (dev only)</span>
      {error && <span style={{ color: '#fca5a5', fontSize: 12 }}>{error}</span>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {!recording ? (
          <Button type="button" onClick={startRecording}>
            Start recording
          </Button>
        ) : (
          <Button type="button" onClick={stopRecording}>
            Stop recording
          </Button>
        )}
        {downloadUrl && (
          <>
            <DownloadLink
              href={downloadUrl}
              download={`session-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.webm`}
            >
              Download .webm
            </DownloadLink>
            <Button type="button" onClick={clearDownload}>
              Clear
            </Button>
          </>
        )}
      </div>
    </Panel>
  );
};

const SessionRecorder: React.FC = () => {
  if (!RECORDING_ENABLED) return null;
  return <SessionRecorderInner />;
};

export default SessionRecorder;
export { RECORDING_ENABLED };
